import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { createAddendum, updateAddendum, getAddendumPrefill } from "./api";
import { toYmd } from "../../../../../../utils/dateOnly";
import {
  deriveTenantNames,
  pairEmailsFromLease,
  rowsFromExisting,
  seedTenantRows,
} from "./tenantPrefill";

// Parse a stored fee to a number. Mastersheet fees may be plain numbers or
// currency-formatted strings ("$50.00", "1,050"); strip formatting before parsing
// so a string value like Telus's still prefills its real amount, not 0.
const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const cleaned = String(v).replace(/[$£€¥₹,\s]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The only fees an addendum may amend. field_key is the canonical MasterSheet
// key; `aliases` are alternate storage keys some property layouts use;
// `tenant_name` autofills the tenant-facing "Name shown to tenant";
// `one_time` fees are charged once and never counted in the monthly rent total.
const FEE_FIELDS = [
  { field_key: "telus", label: "Telus Fee", tenant_name: "Telus Service Fee", aliases: ["telusFee"] },
  { field_key: "storageFee", label: "Storage Fee", tenant_name: "Storage Fee" },
  { field_key: "ssFee", label: "SS Fee", tenant_name: "Surface Stall Fee", aliases: ["surfaceStallFee"] },
  { field_key: "ugFee", label: "UG Fee", tenant_name: "Underground Fee", aliases: ["undergroundFee"] },
  { field_key: "extraTenantFee", label: "Extra Tenant Fee", tenant_name: "Extra Tenant Fee" },
  { field_key: "dogFee", label: "Dog Fee", tenant_name: "Dog Fee" },
  { field_key: "catFee", label: "Cat Fee", tenant_name: "Cat Fee" },
  { field_key: "acFee", label: "AC Charges", tenant_name: "AC Charges" },
  { field_key: "psfNonRefundable", label: "PSF Non Refundable", tenant_name: "Pet Security Fee (One-Time, Non-Refundable)", one_time: true },
];
// Fields that make up the monthly rent (drive the live total).
const MONTHLY_CHARGE_KEYS = ["basicRent", "ssFee", "ugFee", "dogFee", "catFee", "otherPetFee", "storageFee", "otherFee", "acFee", "telus", "extraTenantFee"];

const normLabel = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const hasValue = (v) => v !== undefined && v !== null && v !== "";

// Flatten a property's (possibly grouped) mastersheet field layout to {key,label}.
const flattenLayout = (fields) => {
  const out = [];
  const walk = (arr) =>
    (arr || []).forEach((f) => {
      if (f && f.type === "group" && Array.isArray(f.fields)) return walk(f.fields);
      if (f && f.key) out.push({ key: f.key, label: f.label || f.key });
    });
  walk(fields);
  return out;
};

// A property layout may store a fee under a custom key/label (e.g. a "Telus Fee"
// column keyed differently than `telus`). Resolve the key that actually holds
// this fee's value on the item so "Current" matches the mastersheet — and the
// write-back targets that same key.
const resolveFeeKey = (item, flatFields, fee) => {
  const wanted = normLabel(fee.label);
  const candidates = [fee.field_key, ...(fee.aliases || [])];
  flatFields.forEach((f) => {
    if (normLabel(f.label) === wanted && !candidates.includes(f.key)) candidates.push(f.key);
  });
  return candidates.find((k) => hasValue(item[k])) || fee.field_key;
};

// Map a stored key back to its whitelist option id (for the dropdown selection).
const canonicalPick = (key) => {
  const fee = FEE_FIELDS.find((f) => f.field_key === key || (f.aliases || []).includes(key));
  return fee ? fee.field_key : key || "";
};

const deriveAction = (oldVal, newVal) => {
  const o = num(oldVal);
  const n = num(newVal);
  if (n === 0 && o > 0) return "removed";
  if (!o && n > 0) return "added";
  return "updated";
};

// Normalize a stored amendment row (legacy rows may carry `change` instead of a total).
const normalizeRow = (r, addendumEffectiveDate = "") => {
  // Rows saved before per-item dates inherit the addendum-level date.
  const effective = toYmd(r.effective_date) || toYmd(addendumEffectiveDate) || "";
  if (r.type === "text") {
    return {
      pick: canonicalPick(r.field_key),
      field_key: r.field_key || "",
      label: r.label || "",
      display_name: r.display_name || "",
      type: "text",
      old_value: r.old_value ?? "",
      old_quantity: "",
      quantity: "",
      new_value: r.new_value ?? "",
      one_time: false,
      effective_date: effective,
      applied: !!r.applied_at,
    };
  }
  return {
    pick: canonicalPick(r.field_key),
    field_key: r.field_key || "",
    label: r.label || "",
    display_name: r.display_name || "",
    type: "number",
    old_value: num(r.old_value),
    old_quantity: r.old_quantity === undefined || r.old_quantity === null ? "" : r.old_quantity,
    quantity: r.quantity === undefined || r.quantity === null ? "" : r.quantity,
    new_value:
      r.new_value === undefined || r.new_value === null || r.new_value === ""
        ? r.change !== undefined
          ? num(r.old_value) + num(r.change)
          : ""
        : num(r.new_value),
    one_time: !!r.one_time || r.field_key === "psfNonRefundable",
    effective_date: effective,
    applied: !!r.applied_at,
  };
};

/* ── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  iconBg: "#EBF3FC",
  card: "#FFFFFF",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
};

// One type scale for the whole modal. Only four steps, each a small increment on
// the last — big jumps between a tiny label and a much larger value are what make
// a dense form look unfinished.
const T = { title: 17, section: 14.5, body: 13.5, label: 12, hint: 11.5 };

// Grid template shared by the amendment header row and every amendment row, so
// the columns line up exactly.
const AMEND_COLS =
  "minmax(150px, 1.35fr) minmax(150px, 1.4fr) 68px 92px 68px 104px 138px 30px";

const styles = {
  body: { padding: "14px 20px 12px", maxHeight: "74vh", overflowY: "auto", background: "#F7F9FC" },
  section: { marginBottom: 12 },
  sectionTitle: { fontSize: T.section, fontWeight: 700, color: C.text, lineHeight: 1.3 },
  label: { fontSize: T.label, fontWeight: 600, color: C.muted, marginBottom: 5, display: "block" },
  colLabel: {
    fontSize: T.label, fontWeight: 600, color: C.muted,
    display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 8, height: 38,
    border: `1px solid ${C.inputBorder}`, borderRadius: 8, padding: "0 10px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  card: {
    border: `1px solid ${C.border}`, borderRadius: 12, background: "#fff",
    boxShadow: "0 1px 2px rgba(15,31,53,0.04)", overflow: "hidden",
  },
  cardHead: {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: "#FBFCFE",
  },
  cardBody: { padding: 14 },
  readOnly: { background: "#F4F7FB", color: C.text },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.45 },
};

/** Small tinted icon tile used to head each section. */
const SectionIcon = ({ icon }) => (
  <span
    style={{
      width: 28, height: 28, borderRadius: 8, background: C.iconBg, flex: "0 0 auto",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}
  >
    <FeatherIcon icon={icon} size={14} style={{ color: C.primary }} />
  </span>
);

const IconBadge = ({ icon, size = 44, round = 12 }) => (
  <div style={{ width: size, height: size, borderRadius: round, background: C.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
    <FeatherIcon icon={icon} size={Math.round(size * 0.46)} style={{ color: C.primary }} />
  </div>
);

const Sparkle = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={C.primaryMd} aria-hidden="true">
    <path d="M12 2.5l1.6 4.3 4.3 1.6-4.3 1.6L12 14.3l-1.6-4.3L6.1 8.4l4.3-1.6z" />
    <path d="M18.5 13.5l.8 2.1 2.1.8-2.1.8-.8 2.1-.8-2.1-2.1-.8 2.1-.8z" />
  </svg>
);

/**
 * Create / edit a Lease Addendum. The amendment section is a generic repeater:
 * pick a mastersheet field by its label, enter the change (numbers compute the
 * new value live), optionally give it a clearer tenant-facing name. The new
 * monthly total recomputes automatically.
 */
const AddendumModal = ({
  isOpen,
  toggle,
  itemId,
  item = {},
  tenants = [],
  fields = [],
  buildingName,
  propertyName = "",
  unitNo,
  defaults = {},
  existing,
  onSaved,
}) => {
  const propertyAddress = `${propertyName ? propertyName + " " : ""}${buildingName || ""}`.trim();
  const headerAddress = [unitNo, buildingName].filter(Boolean).join(" - ") || propertyAddress || "—";

  const flatFields = useMemo(() => flattenLayout(fields), [fields]);

  const prefill = useMemo(() => {
    if (existing) {
      return {
        tenant_rows: rowsFromExisting(existing),
        original_lease_date: toYmd(existing.original_lease_date),
        effective_date: toYmd(existing.effective_date),
        lease_end_date: toYmd(existing.lease_end_date || item.leaseEndDmy),
        amendments: (Array.isArray(existing.amendments) ? existing.amendments : []).map((r) =>
          normalizeRow(r, existing.effective_date)
        ),
      };
    }
    return {
      tenant_rows: seedTenantRows(item, tenants),
      original_lease_date: toYmd(item.leaseStartDmy),
      effective_date: "",
      lease_end_date: toYmd(item.leaseEndDmy),
      amendments: [],
    };
  }, [existing, item, tenants]);

  // Whitelisted fees, plus any legacy field already on this draft (kept selectable
  // only for its own row).
  const pickList = useMemo(() => {
    const seen = new Set();
    const extras = (prefill.amendments || [])
      .filter((r) => (r.pick || r.field_key) && !FEE_FIELDS.some((f) => f.field_key === (r.pick || r.field_key)))
      .filter((r) => { const id = r.pick || r.field_key; return seen.has(id) ? false : seen.add(id); })
      .map((r) => ({ field_key: r.pick || r.field_key, label: r.label || r.field_key, legacy: true }));
    return [...FEE_FIELDS, ...extras];
  }, [prefill.amendments]);

  const [form, setForm] = useState(prefill);
  const [saving, setSaving] = useState(false);
  // Latest active lease for this unit — the source of truth for who is on the
  // addendum (names + emails) and for the lease dates shown on it.
  const [leaseInfo, setLeaseInfo] = useState(null);
  // Once the manager edits the tenant rows, the lease prefill must never
  // replace what they typed.
  const tenantsTouchedRef = useRef(false);

  const tenantRows = form.tenant_rows || [];
  // Memoised so the live-total useMemo below does not see a new array identity on
  // every render.
  const amendments = useMemo(() => form.amendments || [], [form.amendments]);

  // Dates are read-only and resolved lease-first — the latest active lease is
  // authoritative — with the mastersheet as fallback. They only become editable
  // when neither source has a value, so an addendum can never be stranded
  // without dates.
  const msOriginal = toYmd(item.leaseStartDmy);
  const msEnd = toYmd(item.leaseEndDmy);
  const originalDate = leaseInfo?.original_lease_date || msOriginal || "";
  const endDate = leaseInfo?.lease_end_date || msEnd || "";
  const originalLocked = !!originalDate;
  const endLocked = !!endDate;
  const originalValue = originalLocked ? originalDate : toYmd(form.original_lease_date);
  const endValue = endLocked ? endDate : toYmd(form.lease_end_date);
  const originalOrigin = leaseInfo?.original_lease_date ? "lease" : msOriginal ? "mastersheet" : "";
  const endOrigin = leaseInfo?.lease_end_date ? "lease" : msEnd ? "mastersheet" : "";
  // The two dates can resolve from different places, so the badge names what
  // actually supplied them rather than assuming.
  const lockedDatesOrigin =
    originalOrigin && endOrigin && originalOrigin !== endOrigin
      ? "From the lease & mastersheet"
      : `From the ${originalOrigin || endOrigin || "tenancy"}`;

  useEffect(() => {
    if (!isOpen) return;
    const propertyId = defaults.property_id;
    if (!propertyId || !unitNo) return;
    let active = true;
    getAddendumPrefill({ property_id: propertyId, unit_no: unitNo, building_name: buildingName })
      .then((res) => {
        if (!active) return;
        const data = res?.data?.data;
        if (!data) return;
        setLeaseInfo(data);
        setForm((prev) => {
          const leaseTenants = (data.tenants || []).filter((t) => t.name || t.email);
          // New addendum: the latest active lease is authoritative for who is
          // on the document — replace the mastersheet seed wholesale, as long
          // as the manager has not edited the rows yet.
          if (!existing && leaseTenants.length && !tenantsTouchedRef.current) {
            return {
              ...prev,
              tenant_rows: leaseTenants.map((t) => ({ name: t.name, email: t.email })),
            };
          }
          // Otherwise only fill blanks — never overwrite what the manager typed.
          return {
            ...prev,
            tenant_rows: pairEmailsFromLease(prev.tenant_rows || [], data.tenants),
          };
        });
      })
      .catch(() => {
        /* prefill is best-effort; the manager can always type the details in */
      });
    return () => {
      active = false;
    };
  }, [isOpen, defaults.property_id, unitNo, buildingName, existing]);

  const setRow = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      amendments: prev.amendments.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));

  const addAmendment = () =>
    setForm((prev) => ({
      ...prev,
      amendments: [
        ...(prev.amendments || []),
        { pick: "", field_key: "", label: "", display_name: "", type: "number", old_value: 0, old_quantity: "", quantity: "", new_value: "", one_time: false, effective_date: "" },
      ],
    }));

  const removeAmendment = (i) =>
    setForm((prev) => ({ ...prev, amendments: prev.amendments.filter((_, idx) => idx !== i) }));

  const onPickField = (i, pickKey) => {
    const entry = pickList.find((p) => p.field_key === pickKey);
    if (!entry) {
      setRow(i, { pick: "", field_key: "", label: "", display_name: "", type: "number", old_value: 0, old_quantity: "", quantity: "", new_value: "", one_time: false });
      return;
    }
    const storeKey = entry.legacy ? entry.field_key : resolveFeeKey(item, flatFields, entry);
    setRow(i, {
      pick: entry.field_key,
      field_key: storeKey,
      label: entry.label,
      display_name: entry.tenant_name || entry.label,
      type: "number",
      old_value: num(item[storeKey]),
      old_quantity: "",
      quantity: "",
      new_value: "",
      one_time: !!entry.one_time,
    });
  };

  // Live new monthly total: current charges with amended values applied.
  const newTotal = useMemo(() => {
    const base = MONTHLY_CHARGE_KEYS.reduce((s, k) => {
      const fee = FEE_FIELDS.find((f) => f.field_key === k);
      const sk = fee ? resolveFeeKey(item, flatFields, fee) : k;
      return s + num(item[sk]);
    }, 0);
    let total = base;
    amendments.forEach((r) => {
      if (
        r.type !== "text" &&
        !r.one_time &&
        MONTHLY_CHARGE_KEYS.includes(r.pick || r.field_key) &&
        r.new_value !== "" && r.new_value !== null && r.new_value !== undefined
      ) {
        total += num(r.new_value) - num(r.old_value);
      }
    });
    return total;
  }, [amendments, item, flatFields]);

  const setTenantRow = (i, patch) => {
    tenantsTouchedRef.current = true;
    setForm((prev) => ({
      ...prev,
      tenant_rows: (prev.tenant_rows || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));
  };

  const addTenantRow = () => {
    tenantsTouchedRef.current = true;
    setForm((prev) => ({ ...prev, tenant_rows: [...(prev.tenant_rows || []), { name: "", email: "" }] }));
  };

  const removeTenantRow = (i) => {
    tenantsTouchedRef.current = true;
    setForm((prev) => ({ ...prev, tenant_rows: (prev.tenant_rows || []).filter((_, idx) => idx !== i) }));
  };

  // Re-pair emails from the lease, filling only the rows still blank.
  const autofillFromLease = () => {
    const leaseTenants = leaseInfo?.tenants || [];
    if (!leaseTenants.length) {
      toast.info("No lease with tenant emails found for this unit");
      return;
    }
    setForm((prev) => {
      const next = pairEmailsFromLease(prev.tenant_rows || [], leaseTenants);
      // Nothing to pair against yet — seed the rows straight from the lease.
      const empty = next.every((r) => !String(r.name || "").trim() && !String(r.email || "").trim());
      return {
        ...prev,
        tenant_rows: empty ? leaseTenants.map((t) => ({ name: t.name, email: t.email })) : next,
      };
    });
    toast.success("Filled from the lease we sent earlier");
  };

  const handleSave = async () => {
    const namedTenants = tenantRows.filter((r) => String(r.name || "").trim());
    if (!namedTenants.length) {
      toast.error("Add at least one tenant");
      return;
    }
    const cleaned = amendments.filter((r) => r.field_key);
    if (!cleaned.length) {
      toast.error("Add at least one amendment");
      return;
    }
    const missingDate = cleaned.find((r) => !toYmd(r.effective_date));
    if (missingDate) {
      toast.error(`Set the effective date for ${missingDate.display_name || missingDate.label}`);
      return;
    }
    const missingCharge = cleaned.find(
      (r) => r.type !== "text" && (r.new_value === "" || r.new_value === null || r.new_value === undefined)
    );
    if (missingCharge) {
      toast.error(`Enter the total charge for ${missingCharge.display_name || missingCharge.label}`);
      return;
    }
    const negative = cleaned.find((r) => r.type !== "text" && num(r.new_value) < 0);
    if (negative) {
      toast.error(`Total charge for ${negative.display_name || negative.label} can't be negative`);
      return;
    }
    setSaving(true);
    try {
      // The repeater is the signer list — preserve any signature already captured
      // so re-saving a draft never wipes a tenant's signature.
      const tenantSignatures = namedTenants.map((r, idx) => ({
        tenant_index: idx,
        name: String(r.name || "").trim(),
        email: String(r.email || "").trim(),
        signed: !!r.signed,
        signature: r.signature || "",
        date: r.date || "",
      }));

      const outAmendments = cleaned.map((r) => {
        if (r.type === "text") {
          return {
            field_key: r.field_key,
            label: r.label,
            display_name: (r.display_name || "").trim(),
            type: "text",
            action: "updated",
            old_value: r.old_value,
            new_value: r.new_value,
            effective_date: toYmd(r.effective_date),
          };
        }
        return {
          field_key: r.field_key,
          label: r.label,
          display_name: (r.display_name || "").trim(),
          type: "number",
          action: deriveAction(r.old_value, r.new_value),
          old_value: num(r.old_value),
          ...(r.old_quantity === "" || r.old_quantity === null || r.old_quantity === undefined
            ? {}
            : { old_quantity: num(r.old_quantity) }),
          ...(r.quantity === "" || r.quantity === null || r.quantity === undefined
            ? {}
            : { quantity: num(r.quantity) }),
          new_value: num(r.new_value),
          ...(r.one_time ? { one_time: true } : {}),
          effective_date: toYmd(r.effective_date),
        };
      });

      // The addendum-level date is derived: the earliest per-item start date.
      const earliestEffective = outAmendments.map((r) => r.effective_date).filter(Boolean).sort()[0] || "";

      const payload = {
        mastersheet_item_id: itemId,
        property_id: defaults.property_id,
        building_name: buildingName,
        unit_no: unitNo,
        property_address: propertyAddress,
        tenant_names: deriveTenantNames(namedTenants),
        original_lease_date: originalValue || undefined,
        effective_date: earliestEffective,
        lease_end_date: endValue || "",
        amendments: outAmendments,
        revised_rent: { new_total_monthly_rent: newTotal },
        tenant_signatures: tenantSignatures,
      };
      if (existing?._id) {
        await updateAddendum(existing._id, payload);
        toast.success("Addendum updated");
      } else {
        await createAddendum(payload);
        toast.success("Addendum created");
      }
      onSaved && onSaved();
      toggle();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save addendum");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered contentClassName="addendum-modal-content">
      <style>{`
        .addendum-modal-content { border: none; border-radius: 16px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .addendum-modal-content input::placeholder { color: #9AA7B8; }
        .am-link { background: none; border: none; padding: 0; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: ${T.label}px; font-weight: 600; color: ${C.primaryMd}; }
        .am-link:hover { color: ${C.primary}; }
        .am-add { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 7px 14px; margin-top: 10px;
          border: 1px dashed ${C.inputBorder}; border-radius: 8px; background: #fff; cursor: pointer;
          font-size: ${T.label}px; font-weight: 600; color: ${C.primary}; transition: background .15s, border-color .15s; }
        .am-add:hover { background: ${C.iconBg}; border-color: ${C.primaryMd}; }
        .am-row { display: grid; grid-template-columns: ${AMEND_COLS}; gap: 10px; align-items: center; }
        .am-amend { border: 1px solid ${C.border}; border-radius: 8px; background: #FDFEFF; padding: 8px 10px; margin-top: 8px; }
        .am-amend:hover { border-color: #C9D6E8; }
        .addendum-modal-content select:focus, .addendum-modal-content input:focus { outline: none; }
        .am-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.14); }
        .am-btn { border: none; cursor: pointer; font-size: 13.5px; font-weight: 600; border-radius: 8px; padding: 9px 18px; display: inline-flex; align-items: center; gap: 7px; }
        .am-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .am-btn-primary:hover:not(:disabled) { background: #083460; }
        .am-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .am-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .am-btn-ghost:hover { background: #F1F5FA; }
        .am-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .am-iconbtn:hover { color: #991B1B; }
      `}</style>

      {/* Header — carries the unit so no separate property card is needed. */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "14px 20px",
          borderBottom: `1px solid ${C.border}`, background: "#fff",
        }}
      >
        <IconBadge icon="file-text" size={38} round={10} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>
            {existing ? "Edit lease addendum" : "Create lease addendum"}
          </div>
          <div style={{ fontSize: T.label, fontWeight: 500, color: C.muted, marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}>
            <FeatherIcon icon="home" size={12} style={{ flex: "0 0 auto" }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {headerAddress}
            </span>
          </div>
        </div>
        <button className="am-iconbtn" style={{ color: C.muted }} onClick={toggle} title="Close">
          <FeatherIcon icon="x" size={20} />
        </button>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {/* Lease dates — read-only meta, so shown as a strip rather than form fields. */}
        {originalLocked && endLocked ? (
          // Label and value sit at the same size so the strip reads evenly.
          <div
            style={{
              display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
              background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12,
              boxShadow: "0 1px 2px rgba(15,31,53,0.04)",
              padding: "10px 14px", marginBottom: 12,
            }}
          >
            <span
              title="These dates belong to the tenancy and cannot be changed on an addendum"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, flex: "0 0 auto",
                fontSize: T.hint, fontWeight: 600, color: C.muted,
                background: "#F1F5FA", border: `1px solid ${C.border}`,
                borderRadius: 999, padding: "3px 9px",
              }}
            >
              <FeatherIcon icon="lock" size={11} /> {lockedDatesOrigin}
            </span>
            {[
              { label: "Original lease", value: originalValue },
              { label: "Lease end", value: endValue },
            ].map((d) => (
              <span
                key={d.label}
                style={{ display: "inline-flex", alignItems: "baseline", gap: 7, fontSize: T.body }}
              >
                <span style={{ color: C.muted, fontWeight: 500 }}>{d.label}</span>
                <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {d.value}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ ...styles.card, ...styles.cardBody, display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            {[
              { key: "original_lease_date", label: "Original lease date", value: originalValue, locked: originalLocked },
              { key: "lease_end_date", label: "Lease end date", value: endValue, locked: endLocked },
            ].map((d) => (
              <div key={d.key} style={{ flex: "1 1 200px" }}>
                <span style={styles.label}>{d.label}</span>
                <div className="am-focusable" style={{ ...styles.fieldBox, ...(d.locked ? styles.readOnly : {}) }}>
                  <FeatherIcon icon={d.locked ? "lock" : "calendar"} size={15} style={{ color: C.muted, flex: "0 0 auto" }} />
                  <input
                    type="date"
                    style={{ ...styles.bareInput, ...(d.locked ? { color: C.muted } : {}) }}
                    value={d.value}
                    readOnly={d.locked}
                    disabled={d.locked}
                    onChange={(e) => setForm((p) => ({ ...p, [d.key]: e.target.value }))}
                  />
                </div>
                {!d.locked && <div style={{ ...styles.hint, marginTop: 4 }}>Not on file — enter it manually</div>}
              </div>
            ))}
          </div>
        )}

        {/* Tenants — one row per signer; this list drives who receives a signing link. */}
        <div style={{ ...styles.card, ...styles.section }}>
          <div style={styles.cardHead}>
            <SectionIcon icon="users" />
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={styles.sectionTitle}>Tenant(s)</div>
              <div style={styles.hint}>Each tenant signs separately via their own link.</div>
            </div>
            <button className="am-link" onClick={autofillFromLease}>
              <Sparkle /> Autofill from lease
            </button>
          </div>

          <div style={styles.cardBody}>
            {/* Column labels once, so each row stays a single line. */}
            <div style={{ display: "flex", gap: 10, paddingLeft: 26, marginBottom: 4 }}>
              <span style={{ ...styles.colLabel, flex: "1 1 200px" }}>Name</span>
              <span style={{ ...styles.colLabel, flex: "1 1 240px" }}>Email</span>
              <span style={{ width: 20, flex: "0 0 auto" }} />
            </div>

            {tenantRows.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                <span style={{ width: 20, textAlign: "right", fontSize: T.label, fontWeight: 600, color: C.muted, flex: "0 0 auto" }}>
                  {i + 1}
                </span>
                <div className="am-focusable" style={{ ...styles.fieldBox, flex: "1 1 200px" }}>
                  <FeatherIcon icon="user" size={14} style={{ color: C.muted, flex: "0 0 auto" }} />
                  <input
                    aria-label={`Tenant ${i + 1} name`}
                    style={styles.bareInput}
                    placeholder="Tenant name"
                    value={row.name || ""}
                    onChange={(e) => setTenantRow(i, { name: e.target.value })}
                  />
                </div>
                <div className="am-focusable" style={{ ...styles.fieldBox, flex: "1 1 240px" }}>
                  <FeatherIcon icon="mail" size={14} style={{ color: C.muted, flex: "0 0 auto" }} />
                  <input
                    type="email"
                    aria-label={`Tenant ${i + 1} email`}
                    style={styles.bareInput}
                    placeholder="name@example.com"
                    value={row.email || ""}
                    onChange={(e) => setTenantRow(i, { email: e.target.value })}
                  />
                </div>
                {row.signed ? (
                  <span title="Already signed" style={{ width: 20, flex: "0 0 auto", color: "#16A34A", display: "inline-flex" }}>
                    <FeatherIcon icon="check-circle" size={16} />
                  </span>
                ) : tenantRows.length > 1 ? (
                  <button className="am-iconbtn" style={{ width: 20, flex: "0 0 auto" }} title="Remove tenant" onClick={() => removeTenantRow(i)}>
                    <FeatherIcon icon="trash-2" size={15} />
                  </button>
                ) : (
                  <span style={{ width: 20, flex: "0 0 auto" }} />
                )}
              </div>
            ))}

            <button className="am-link" style={{ marginLeft: 26 }} onClick={addTenantRow}>
              <FeatherIcon icon="plus" size={14} /> Add tenant
            </button>
          </div>
        </div>

        {/* Amendment details — every charge carries its own effective date. */}
        <div style={styles.card}>
          <div style={styles.cardHead}>
            <SectionIcon icon="edit-2" />
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={styles.sectionTitle}>Amendment details</div>
              <div style={styles.hint}>
                Pick the charges this addendum changes — each has its own effective date.
              </div>
            </div>
          </div>

          <div style={styles.cardBody}>
            {amendments.length === 0 ? (
              <div
                style={{
                  textAlign: "center", padding: "18px 14px",
                  border: `1px dashed ${C.inputBorder}`, borderRadius: 10, background: "#FBFCFE",
                }}
              >
                <div style={{ fontSize: T.body, fontWeight: 600, color: C.text }}>
                  No amendments yet
                </div>
                <div style={{ ...styles.hint, marginTop: 3 }}>
                  Add the fields you want this addendum to change.
                </div>
                <button className="am-add" onClick={addAmendment}>
                  <FeatherIcon icon="plus" size={15} /> Add amendment
                </button>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: 880 }}>
                  {/* One header row for the grid — every amendment row uses the
                      same column template, so they stay aligned. */}
                  <div className="am-row" style={{ padding: "0 10px 2px" }}>
                    <span style={styles.colLabel}>Field</span>
                    <span style={styles.colLabel}>Shown to tenant</span>
                    <span style={styles.colLabel}>Cur qty</span>
                    <span style={styles.colLabel}>Cur $</span>
                    <span style={styles.colLabel}>New qty</span>
                    <span style={styles.colLabel}>New $</span>
                    <span style={styles.colLabel}>Effective date</span>
                    <span />
                  </div>

                  {amendments.map((row, i) => {
                    const isText = row.field_key && row.type === "text";
                    return (
                      <div key={i} className="am-amend">
                        <div className="am-row">
                          <select
                            aria-label="Field to amend"
                            style={{ width: "100%", height: 38, border: `1px solid ${C.inputBorder}`, borderRadius: 8, padding: "0 8px", background: "#fff", fontSize: T.body, color: C.text, cursor: "pointer", fontFamily: "inherit" }}
                            value={row.pick || ""}
                            onChange={(e) => onPickField(i, e.target.value)}
                          >
                            <option value="">Select a field…</option>
                            {pickList.map((p) => (
                              <option key={p.field_key} value={p.field_key} disabled={!!p.legacy && (row.pick || row.field_key) !== p.field_key}>
                                {p.label}
                              </option>
                            ))}
                          </select>

                          <div className="am-focusable" style={styles.fieldBox}>
                            <input
                              aria-label="Name shown to tenant"
                              style={styles.bareInput}
                              placeholder={row.label || "e.g. Surface stall fee"}
                              value={row.display_name || ""}
                              onChange={(e) => setRow(i, { display_name: e.target.value })}
                            />
                          </div>

                          {isText ? (
                            <>
                              <div style={{ ...styles.fieldBox, ...styles.readOnly, gridColumn: "span 2" }}>
                                {row.old_value === "" ? "—" : String(row.old_value)}
                              </div>
                              <div style={{ ...styles.fieldBox, ...styles.readOnly, gridColumn: "span 2" }}>
                                {row.new_value === "" ? "—" : String(row.new_value)}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="am-focusable" style={styles.fieldBox}>
                                <input type="number" min="0" aria-label="Current quantity" style={styles.bareInput} placeholder="1" value={row.old_quantity} onChange={(e) => setRow(i, { old_quantity: e.target.value })} />
                              </div>
                              <div style={{ ...styles.fieldBox, ...styles.readOnly, fontVariantNumeric: "tabular-nums" }}>
                                {money(row.old_value)}
                              </div>
                              <div className="am-focusable" style={styles.fieldBox}>
                                <input type="number" min="0" aria-label="New quantity" style={styles.bareInput} placeholder="2" value={row.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} />
                              </div>
                              <div className="am-focusable" style={styles.fieldBox}>
                                <input type="number" min="0" aria-label={row.one_time ? "New one-time amount" : "New monthly amount"} style={styles.bareInput} placeholder="Amount" value={row.new_value} onChange={(e) => setRow(i, { new_value: e.target.value })} />
                              </div>
                            </>
                          )}

                          <div className="am-focusable" style={{ ...styles.fieldBox, ...(row.applied ? styles.readOnly : {}) }}>
                            <FeatherIcon icon="calendar" size={13} style={{ color: C.muted, flex: "0 0 auto" }} />
                            <input
                              type="date"
                              aria-label={`Effective date for ${row.display_name || row.label || "amendment"}`}
                              style={styles.bareInput}
                              value={toYmd(row.effective_date)}
                              readOnly={!!row.applied}
                              disabled={!!row.applied}
                              title={row.applied ? "Already applied to the mastersheet — the date can no longer change" : undefined}
                              onChange={(e) => setRow(i, { effective_date: e.target.value })}
                            />
                          </div>

                          <button className="am-iconbtn" style={{ justifySelf: "center" }} title="Remove amendment" onClick={() => removeAmendment(i)}>
                            <FeatherIcon icon="trash-2" size={16} />
                          </button>
                        </div>

                        {isText && (
                          <div style={{ ...styles.hint, marginTop: 6 }}>
                            Legacy text amendment — kept as saved; it can be removed but not edited here.
                          </div>
                        )}
                        {row.one_time && !isText && (
                          <div style={{ fontSize: T.hint, fontWeight: 500, color: "#854f0b", display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
                            <FeatherIcon icon="info" size={12} style={{ flex: "0 0 auto" }} />
                            One-time fee — never added to the monthly rent total.
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <button className="am-add" onClick={addAmendment}>
                    <FeatherIcon icon="plus" size={15} /> Add amendment
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New total — the one figure that matters, so it carries the accent. */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12, marginTop: 12,
            background: C.primary, borderRadius: 12, padding: "12px 16px",
            boxShadow: "0 6px 18px rgba(12,68,124,0.18)",
          }}
        >
          <span style={{
            width: 30, height: 30, borderRadius: 8, flex: "0 0 auto",
            background: "rgba(255,255,255,0.16)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <FeatherIcon icon="dollar-sign" size={15} style={{ color: "#fff" }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T.body, fontWeight: 600, color: "#fff" }}>
              New total monthly rent payable
            </div>
            <div style={{ fontSize: T.hint, fontWeight: 500, color: "rgba(255,255,255,0.72)", marginTop: 1 }}>
              Each amendment applies from its own effective date once every tenant has signed.
            </div>
          </div>
          <span style={{ fontSize: 20, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {money(newTotal)}
          </span>
        </div>
      </div>

      {/* Footer actions */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          padding: "12px 20px", borderTop: `1px solid ${C.border}`, background: "#fff",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: 1, minWidth: 200 }}>
          <FeatherIcon icon="shield" size={13} style={{ color: "#16A34A", flex: "0 0 auto" }} />
          <span style={styles.hint}>Every listed tenant must e-sign before this takes effect.</span>
        </span>
        <button className="am-btn am-btn-ghost" onClick={toggle} disabled={saving}>Cancel</button>
        <button className="am-btn am-btn-primary" onClick={handleSave} disabled={saving}>
          <FeatherIcon icon="save" size={15} />
          {saving ? "Saving…" : existing ? "Save changes" : "Save draft"}
        </button>
      </div>
    </Modal>
  );
};

export default AddendumModal;
